import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import { useConfig } from '../context/ConfigContext';

const CACHE_BUSTER = Date.now();

const Agenda = () => {
    const [events, setEvents] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [activeGroup, setActiveGroup] = useState(null);
    const [activeSubgroup, setActiveSubgroup] = useState(null);
    const { config } = useConfig();

    useEffect(() => {
        fetchAgenda();
    }, []);

    useEffect(() => {
        if (events.length > 0) {
            const availableGroups = Array.from(new Set(events.map(e => e.group_name).filter(Boolean)));
            if (availableGroups.length > 0 && !activeGroup) {
                setActiveGroup(availableGroups[0]);
            }
        }
    }, [events, activeGroup]);

    useEffect(() => {
        if (events.length > 0 && activeGroup) {
            const activeGroupEvents = events.filter(e => e.group_name === activeGroup);
            const availableSubgroups = Array.from(new Set(activeGroupEvents.map(e => e.subgroup_name).filter(Boolean)));
            if (availableSubgroups.length > 0) {
                setActiveSubgroup(prev => {
                    if (!prev || !availableSubgroups.includes(prev)) {
                        return availableSubgroups[0];
                    }
                    return prev;
                });
            } else {
                setActiveSubgroup(null);
            }
        }
    }, [events, activeGroup]);

    const fetchAgenda = async () => {
        setIsLoading(true);
        try {
            const res = await api.get('/agenda?status=active');
            setEvents(res.data);
        } catch (error) {
            console.error("Error fetching agenda", error);
        } finally {
            setIsLoading(false);
        }
    };

    const pageDesign = config?.pageDesigns?.agenda || {};
    const isImageOnly = pageDesign.agendaImageOnly;

    // Helper functions for flags
    const getFlagEmoji = (country) => {
        if (!country) return '';
        const name = country.trim().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
        const flags = {
            'colombia': '🇨🇴', 'co': '🇨🇴',
            'mexico': '🇲🇽', 'mx': '🇲🇽',
            'argentina': '🇦🇷', 'ar': '🇦🇷',
            'chile': '🇨🇱', 'cl': '🇨🇱',
            'peru': '🇵🇪', 'pe': '🇵🇪',
            'ecuador': '🇪🇨', 'ec': '🇪🇨',
            'espana': '🇪🇸', 'es': '🇪🇸',
            'estados unidos': '🇺🇸', 'usa': '🇺🇸', 'us': '🇺🇸',
            'brasil': '🇧🇷', 'brazil': '🇧🇷', 'br': '🇧🇷',
            'venezuela': '🇻🇪', 've': '🇻🇪',
            'uruguay': '🇺🇾', 'uy': '🇺🇾',
            'paraguay': '🇵🇾', 'py': '🇵🇾',
            'bolivia': '🇧🇴', 'bo': '🇧🇴',
            'panama': '🇵🇦', 'pa': '🇵🇦',
            'costa rica': '🇨🇷', 'cr': '🇨🇷',
            'cuba': '🇨🇺', 'cu': '🇨🇺',
            'republica dominicana': '🇩🇴', 'do': '🇩🇴',
            'puerto rico': '🇵🇷', 'pr': '🇵🇷',
            'honduras': '🇭🇳', 'hn': '🇭🇳',
            'el salvador': '🇸🇻', 'sv': '🇸🇻',
            'guatemala': '🇬🇹', 'gt': '🇬🇹',
            'nicaragua': '🇳🇮', 'ni': '🇳🇮',
            'italia': '🇮🇹', 'it': '🇮🇹',
            'francia': '🇫🇷', 'fr': '🇫🇷',
            'alemania': '🇩🇪', 'de': '🇩🇪',
            'reino unido': '🇬🇧', 'uk': '🇬🇧', 'gb': '🇬🇧',
            'canada': '🇨🇦', 'ca': '🇨🇦'
        };
        return flags[name] || '';
    };

    const groups = Array.from(new Set(events.map(e => e.group_name).filter(Boolean)));
    const activeGroupEvents = activeGroup ? events.filter(e => e.group_name === activeGroup) : events;
    let subgroups = Array.from(new Set(activeGroupEvents.map(e => e.subgroup_name).filter(Boolean)));
    if (pageDesign?.subgroupOrder) {
        const orderArr = pageDesign.subgroupOrder.split(',').map(s => s.trim().toUpperCase());
        subgroups = subgroups.sort((a, b) => {
            const indexA = orderArr.indexOf(a.toUpperCase());
            const indexB = orderArr.indexOf(b.toUpperCase());
            if (indexA !== -1 && indexB !== -1) return indexA - indexB;
            if (indexA !== -1) return -1;
            if (indexB !== -1) return 1;
            return a.localeCompare(b);
        });
    }

    const filteredEvents = events.filter(e => {
        const matchesGroup = !activeGroup || e.group_name === activeGroup;
        const matchesSubgroup = !activeSubgroup || e.subgroup_name === activeSubgroup;
        return matchesGroup && matchesSubgroup;
    });

    const getActiveLegalConfig = (group, subgroup) => {
        const glts = pageDesign.groupLegalTexts || [];
        const normalize = (str) => (str || '').trim().toLowerCase();

        const nGroup = normalize(group);
        const nSub = normalize(subgroup);

        let match = glts.find(g => normalize(g.groupName) === nGroup && normalize(g.subgroupName) === nSub);
        if (match && match.legalTextUrl) return match;

        match = glts.find(g => normalize(g.groupName) === nGroup && !normalize(g.subgroupName));
        if (match && match.legalTextUrl) return match;

        if (nSub) {
            match = glts.find(g => !normalize(g.groupName) && normalize(g.subgroupName) === nSub);
            if (match && match.legalTextUrl) return match;
        }

        return {
            showLegalText: pageDesign.showLegalText,
            legalTextUrl: pageDesign.legalTextUrl
        };
    };

    // Group events by date and block
    const groupedData = filteredEvents.reduce((acc, event) => {
        const dateStr = new Date(event.date).toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' });
        const capitalizedDate = dateStr.charAt(0).toUpperCase() + dateStr.slice(1);

        if (!acc[capitalizedDate]) acc[capitalizedDate] = {};

        const block = event.block_name || 'Sin Bloque';
        if (!acc[capitalizedDate][block]) acc[capitalizedDate][block] = {
            name: block,
            color: event.block_color || '#0a58ca',
            events: []
        };

        const existingEvent = acc[capitalizedDate][block].events.find(e => e.title === event.title && e.start_time === event.start_time);

        if (existingEvent) {
            if (event.speaker_name) {
                existingEvent.speakers = existingEvent.speakers || [];
                if (!existingEvent.speakers.some(s => s.name === event.speaker_name)) {
                    existingEvent.speakers.push({ name: event.speaker_name, country: event.speaker_country || event.country });
                }
            } else if (event.country) {
                existingEvent.speakers = existingEvent.speakers || [];
                if (!existingEvent.speakers.some(s => s.country === event.country)) {
                    existingEvent.speakers.push({ name: '', country: event.country });
                }
            }
        } else {
            const newEvent = { ...event, speakers: [] };
            if (event.speaker_name) {
                newEvent.speakers.push({ name: event.speaker_name, country: event.speaker_country || event.country });
            } else if (event.country) {
                newEvent.speakers.push({ name: '', country: event.country });
            }
            acc[capitalizedDate][block].events.push(newEvent);
        }

        return acc;
    }, {});

    const getIcon = (type) => {
        if (type === 'breakfast') return '🥐';
        if (type === 'coffee') return '☕';
        if (type === 'lunch') return '🍽️';
        if (type === 'closing') return '✈️';
        if (type === 'other') return '✨';
        return null;
    };

    const primaryThemeColor = config?.primaryColor || "#00595b";
    const secondaryThemeColor = config?.secondaryColor || "#ff5874";
    const tertiaryThemeColor = config?.tertiaryColor || "#008b8b";

    const horaHeaderText = pageDesign.hora_header_text || "Hora";
    const temaHeaderText = pageDesign.tema_header_text || "Tema";
    const speakerHeaderText = pageDesign.speaker_header_text || "Conferencista";
    const horaHeaderColor = pageDesign.hora_color || secondaryThemeColor;
    const agendaSubtitle = pageDesign.subtitle || "Miércoles, 19 de agosto";

    return (
        <div className="animate-fadeIn max-w-4xl mx-auto pb-32 font-sans pt-2 px-4 sm:px-6">

            {isLoading ? (
                <div className="flex flex-col items-center justify-center py-20">
                    <div className="w-10 h-10 border-4 border-white/20 border-t-white rounded-full animate-spin"></div>
                    <p className="text-white/60 text-xs font-bold uppercase tracking-widest mt-4">Cargando Agenda...</p>
                </div>
            ) : (
                <div className="space-y-8">
                    {/* Groups toggle if multiple groups configured */}
                    {groups.length > 1 && (
                        <div className="flex justify-center gap-4 w-full overflow-x-auto pb-2 custom-scrollbar">
                            {groups.map(group => {
                                const isSelected = activeGroup === group;
                                return (
                                    <button
                                        key={group}
                                        onClick={() => setActiveGroup(group)}
                                        style={{ backgroundColor: isSelected ? secondaryThemeColor : tertiaryThemeColor }}
                                        className="px-6 py-2.5 rounded-xl font-black uppercase tracking-widest text-[12px] text-white transition-all shadow-md"
                                    >
                                        {group}
                                    </button>
                                );
                            })}
                        </div>
                    )}

                    {Object.keys(groupedData).map((date, dayIdx) => (
                        <div key={dayIdx} className="space-y-4">
                            {/* Table Header Row matching PDF page 3 screen 2 */}
                            <div className="flex w-full gap-1.5 sm:gap-3 select-none items-center overflow-hidden rounded-xl shadow-md text-white font-bold text-xs sm:text-base">
                                <div
                                    className="w-24 sm:w-32 shrink-0 py-2.5 sm:py-3 text-center uppercase tracking-wider"
                                    style={{ backgroundColor: horaHeaderColor }}
                                >
                                    {horaHeaderText}
                                </div>
                                <div
                                    className="flex-1 min-w-0 py-2.5 sm:py-3 text-center uppercase tracking-wider truncate px-1"
                                    style={{ backgroundColor: primaryThemeColor }}
                                >
                                    {temaHeaderText}
                                </div>
                                <div
                                    className="w-24 sm:w-36 shrink-0 py-2.5 sm:py-3 text-center uppercase tracking-wider truncate px-1"
                                    style={{ backgroundColor: primaryThemeColor }}
                                >
                                    {speakerHeaderText}
                                </div>
                            </div>

                            {/* Schedule Rows */}
                            <div className="space-y-2">
                                {Object.values(groupedData[date]).map((block) => (
                                    <React.Fragment key={block.name}>
                                        {block.events.map((event, i) => (
                                            <div
                                                key={i}
                                                className="w-full bg-white/90 backdrop-blur-md rounded-xl p-2.5 sm:p-4 flex flex-row items-center gap-1.5 sm:gap-3 shadow-sm border border-slate-100 min-w-0"
                                            >
                                                {/* Time Capsule */}
                                                <div className="w-24 sm:w-32 shrink-0 flex justify-center">
                                                    <div
                                                        style={{ backgroundColor: tertiaryThemeColor }}
                                                        className="px-1.5 sm:px-3 py-1.5 text-white font-extrabold text-[10px] sm:text-xs rounded-lg text-center shadow-inner w-full tracking-tight"
                                                    >
                                                        {event.start_time.slice(0, 5)} - {event.end_time.slice(0, 5)}
                                                    </div>
                                                </div>

                                                {/* Event Title */}
                                                <div className="flex-1 min-w-0 text-left px-1">
                                                    <h4 className="text-[11px] sm:text-sm font-semibold text-slate-900 leading-tight break-words">
                                                        {event.title}
                                                    </h4>
                                                </div>

                                                {/* Speaker / Moderator */}
                                                <div className="w-24 sm:w-36 shrink-0 text-center font-bold text-[10px] sm:text-sm text-slate-800 break-words leading-tight">
                                                    {event.speaker_name || event.moderator || (event.speakers && event.speakers.map(s => s.name).join(', ')) || '—'}
                                                </div>
                                            </div>
                                        ))}
                                    </React.Fragment>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default Agenda;
